import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Network,
  Boxes,
  Shield,
  User,
  Users,
  Droplets,
  Bell,
  Activity,
  CalendarClock,
} from "lucide-react";

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getRoleBadge = () => {
    switch (user?.role) {
      case "super-admin":
        return {
          label: "Society-admin",
          icon: Shield,
          variant: "default",
          className: "bg-amber-500 text-xs",
        };
      case "aqua-admin":
        return {
          label: "super-admin (aquasense)",
          icon: Shield,
          variant: "default",
          className: "bg-cyan-600 text-xs",
        };
      case "admin":
        return {
          label: "Society-user",
          icon: User,
          variant: "secondary",
          className: "text-xs",
        };
      default:
        return {
          label: user?.role || "User",
          icon: User,
          variant: "secondary",
          className: "text-xs",
        };
    }
  };

  // Navigation items based on role
  const getNavigation = () => {
    const navigation = [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    ];

    // Only Super Admin can see Device Registration
    if (isSuperAdmin()) {
      navigation.push(
        { name: "Setup", href: "/devices", icon: Network },
        { name: "Components", href: "/components", icon: Boxes },
      );
    }

    navigation.push(
      { name: "Analytics", href: "/analytics", icon: Droplets },
      { name: "Sensors", href: "/additional-sensors", icon: Activity },
      { name: "Automation", href: "/automation", icon: CalendarClock },
      { name: "Alerts", href: "/alerts", icon: Bell },
    );

    if (isSuperAdmin()) {
      navigation.push({ name: "Users", href: "/users", icon: Users });
    }

    return navigation;
  };

  const navigation = getNavigation();
  const roleBadge = getRoleBadge();
  const RoleBadgeIcon = roleBadge.icon;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[4.5rem] items-center justify-between gap-4 py-3">
            <Link to="/dashboard" className="flex shrink-0 items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_9595d752-c849-48dc-9f4d-5cf9f8da18fb/artifacts/puwyedcw_logo.png"
                alt="AquaSense"
                className="h-10 w-10 shrink-0"
              />
              <div className="whitespace-nowrap">
                <h1 className="text-xl font-bold leading-tight text-slate-900">
                  AquaTank
                </h1>
                <p className="text-xs text-slate-500">Smart Water Management</p>
              </div>
            </Link>

            {/* Desktop navigation: icons only on laptops, icons + labels on wide screens */}
            <nav className="hidden min-w-0 flex-1 items-center justify-center gap-1 lg:flex">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link key={item.name} to={item.href} title={item.name}>
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      className={`gap-2 px-3 ${isActive ? "bg-primary text-white" : "text-slate-700"}`}
                      aria-label={item.name}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="hidden 2xl:inline">{item.name}</span>
                    </Button>
                  </Link>
                );
              })}
            </nav>

            {/* User menu */}
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <div className="hidden min-w-0 text-right lg:block">
                <div className="flex items-center justify-end gap-2">
                  <p className="max-w-[12rem] truncate whitespace-nowrap text-sm font-semibold text-slate-900">
                    {user?.name || user?.username}
                  </p>
                  <Badge
                    variant={roleBadge.variant}
                    className={`${roleBadge.className} shrink-0 whitespace-nowrap`}
                  >
                    <RoleBadgeIcon className="h-3 w-3 mr-1" />
                    {roleBadge.label}
                  </Badge>
                </div>
                <p className="mt-0.5 max-w-[18rem] truncate text-xs text-slate-500">
                  {user?.email}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2 text-slate-700"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile / tablet navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200">
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {user?.name || user?.username}
                </p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
              </div>
              <Badge
                variant={roleBadge.variant}
                className={`${roleBadge.className} ml-auto shrink-0 whitespace-nowrap`}
              >
                <RoleBadgeIcon className="h-3 w-3 mr-1" />
                {roleBadge.label}
              </Badge>
            </div>
            <nav className="px-4 py-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      className={`w-full justify-start gap-2 ${isActive ? "bg-primary text-white" : "text-slate-700"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
